
import type { PromotionsConfig } from '@empathybroker/tsclient-play/dist/models/instance/domains/tools/promotions-config'
import type { ApiError, ConflictApiError, Promotion } from '@empathyco/playboard-core-domain'
import type { SetupContext } from 'vue'
import type { PromotionsMessages, RegularFilter, ResolveConflictOption } from '../../models'
import type { PromotionDomainService } from '../../services/interfaces/domain/promotion-domain.service'
import { computed, defineComponent, getCurrentInstance, ref, watch } from 'vue'
import ConflictErrorModalWithOptions from '../../components/crud/conflict-error-modal/conflict-error-modal-with-options.component.vue'
import Schedule from '../../components/crud/schedule.component.vue'
import PromotionPreview from '../../components/promotions/promotion-preview.component.vue'
import PromotionSettingAsideForm from '../../components/promotions/promotion-setting-aside-form.component.vue'
import { EpbAlert } from '../../components/shared/epb-alert'
import { EpbKeywords } from '../../components/shared/epb-keywords'
import { EpbSwitch } from '../../components/shared/epb-switch'
import { EpbTextbox } from '../../components/shared/epb-textbox'
import ToolingFormLayout from '../../components/tooling/tooling-form-layout.component.vue'
import ToolingSettingCard from '../../components/tooling/tooling-setting-card.component.vue'
import {
  useApiError,
  useFilters,
  useToolingForm,
  useUpdateItemWithContext,
} from '../../composables'
import { useInstanceToolConfig } from '../../composables/use-instance-tool-config'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { FeatureIdEnum, FilterIdEnum, PositionEnum } from '../../enums'
import { ROUTER_CONSTS } from '../../router/router.consts'
import { FiltersUtils } from '../../utils/filters.utils'

export default defineComponent({
  name: 'PromotionsForm',
  components: {
    EpbAlert,
    EpbSwitch,
    EpbKeywords,
    EpbTextbox,
    PromotionPreview,
    PromotionSettingAsideForm,
    ConflictErrorModalWithOptions,
    Schedule,
    ToolingFormLayout,
    ToolingSettingCard,
  },
  setup(props, ctx: SetupContext) {
    const {
      proxy: { $epb, $route },
    } = getCurrentInstance()
    const { emit } = ctx

    const messages: PromotionsMessages = $epb.messages.tools.promotions

    const updateMode = $route.name === ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION].update

    const { updateItemWithContext } = useUpdateItemWithContext<Promotion>()
    const toolingForm = useToolingForm<Promotion>(props, ctx, {
      messages,
      updateMode,
    })

    const { toolConfig } = useInstanceToolConfig<PromotionsConfig>()
    const { filters } = useFilters()

    const overlapModal = ref(null)
    const isAsideOpen = ref(false)
    const showTOPsDeviceAlert = ref(true)
    const previousPromotionType = ref<string | undefined>(undefined)

    const availableFilters = computed(() => {
      return Object.values(filters.value)
        .filter(filter => toolingForm.visibleFiltersConfig.value[filter.config.id])
        .map((filter: RegularFilter) => {
          const itemFilter = toolingForm.item.value.criteria.pivots.find(
            pivot => pivot.name === FiltersUtils.getEncodedId(filter.config),
          )

          const selectedValues = itemFilter?.values ?? []

          return { ...filter, selectedValues }
        })
    })

    const promotionTypes = computed(() => {
      if (!toolConfig.value.types) {
        return []
      }

      return toolConfig.value.types
    })

    const keywordsHelp = computed<string>(() => {
      if (!toolingForm.item.value) {
        return ''
      }

      return toolingForm.item.value.criteria.matchAll
        ? messages.fields.placeholders.keywords.disabled
        : messages.fields.placeholders.keywords.active
    })

    const domainService = CONTAINER.get<PromotionDomainService>(TYPES.PromotionDomainService)

    const isValid = computed<boolean>(() => {
      return domainService.isValid(toolingForm.item.value)
    })

    const isMobile = computed<boolean>(() => {
      if (domainService.isMobile) {
        return domainService.isMobile(toolingForm.item.value)
      }
      return false
    })

    const isShoppablePromotion = computed<boolean>(() => {
      if (domainService.isShoppablePromotion) {
        return domainService.isShoppablePromotion(toolingForm.item.value.setting)
      }
      return false
    })

    const isRedirectionPromotion = computed<boolean>(() => {
      if (domainService.isRedirectionPromotion) {
        return domainService.isRedirectionPromotion(toolingForm.item.value.setting)
      }
      return false
    })

    const onOpenAside = (): void => {
      isAsideOpen.value = true
    }

    const onCloseAside = (): void => {
      isAsideOpen.value = false
    }

    const onPromotionSettingChange = (promotionSetting: Promotion['setting']): void => {
      toolingForm.item.value = {
        ...toolingForm.item.value,
        setting: promotionSetting,
      }

      onCloseAside()
    }

    const onMatchAllChange = (): void => {
      if (toolingForm.item.value.criteria.matchAll) {
        toolingForm.item.value.criteria.queries = []
      }
    }

    const saveConflictApiError = ref<ConflictApiError>(undefined)
    const { getErrorMessage, isConflictError } = useApiError()

    function errorHandler(error: ApiError): void {
      if (isConflictError(error)) {
        saveConflictApiError.value = error as ConflictApiError
        overlapModal.value.open()
      } else {
        emit('showError', getErrorMessage(error))
      }
    }

    function onAccept(): void {
      toolingForm.onAccept({
        errorHandler,
      })
    }

    const onResolveConflict = (resolveConflictOption: ResolveConflictOption): void => {
      toolingForm.resolveConflict({ resolveConflictOption, errorHandler })
    }

    const initializeItemPivots = (): void => {
      const langFilter = filters.value[FilterIdEnum.LANG] as RegularFilter

      if (
        !toolingForm.item.value.criteria.pivots.length &&
        langFilter?.config.values.length === 1
      ) {
        const [firstValue] = langFilter.config.values

        toolingForm.item.value.criteria.pivots.push({
          name: FilterIdEnum.LANG,
          values: [firstValue.id],
        })
      }
    }

    toolingForm.initItem({ forceFetch: true }).finally(() => {
      initializeItemPivots()
      const isCreationMode = !updateMode

      if (isCreationMode && !toolingForm.isCloneMode()) {
        updateItemWithContext(toolingForm.item)
      }
    })

    const settingError = computed(() => {
      const isUpdating = updateMode || !domainService.isEmptySetting(toolingForm.item.value.setting)
      return isUpdating && !domainService.isSettingValid(toolingForm.item.value.setting)
        ? messages.settings.error
        : undefined
    })

    const closeAlert = (): void => {
      showTOPsDeviceAlert.value = false
    }
    /*
     * This is a temporary solution for Kroger bussiness requirements.
     * This is only applied if the `useDefaultDevicesTops` config is enabled.
     */
    const applyDeviceFilterDefaults = () => {
      if (!$epb.useDefaultDevicesTops) return

      const deviceFilter = Object.values(filters.value).find(
        filter => filter.config.id === FilterIdEnum.DEVICE,
      ) as RegularFilter

      const itemFilterIndex = toolingForm.item.value.criteria.pivots.findIndex(
        pivot => pivot.name === FiltersUtils.getEncodedId(deviceFilter.config),
      )

      let defaultValues: string[] = []

      if (isShoppablePromotion.value) {
        defaultValues = []
      } else if (isRedirectionPromotion.value) {
        defaultValues = ['Web']
      }

      if (itemFilterIndex >= 0) {
        toolingForm.item.value.criteria.pivots[itemFilterIndex].values = defaultValues
      } else {
        toolingForm.item.value.criteria.pivots.push({
          name: FiltersUtils.getEncodedId(deviceFilter.config),
          values: defaultValues,
        })
      }
    }

    watch(
      () => toolingForm.item.value.setting?.type,
      newType => {
        if (newType !== previousPromotionType.value) {
          applyDeviceFilterDefaults()
          previousPromotionType.value = newType
        }
      },
    )

    return {
      baseClass: 'epb-promotions-form',
      promotionsUpdateRoute: ROUTER_CONSTS.routes[FeatureIdEnum.PROMOTION].update,
      ...toolingForm,
      availableFilters,
      filters,
      FilterIdEnum,
      isAsideOpen,
      isMobile,
      isValid,
      keywordsHelp,
      messages,
      onAccept,
      onCloseAside,
      onMatchAllChange,
      onOpenAside,
      onPromotionSettingChange,
      onResolveConflict,
      overlapModal,
      PositionEnum,
      promotionTypes,
      saveConflictApiError,
      settingError,
      updateMode,
      showTOPsDeviceAlert,
      closeAlert,
    }
  },
})
